import { useQuery } from "@tanstack/react-query";
import qs from "qs";
import { strapiGetSingle } from "@/lib/strapi";
import { resolveMediaUrl } from "@/utils/imageHelper";

type MenuItem = {
	menuName: string;
	menuLink: string;
	id?: number | string;
};

type RawSetting = {
	Title: string;
	websiteDescription: string;
	logo: {
		url: string;
		alternativeText: string | null;
		width: number;
		height: number;
	} | null;
	footerMenu: MenuItem[];
	doulaMenu: MenuItem[];
	privacyMenu: MenuItem[];
};

export type SiteSettings = {
	title: string;
	websiteDescription: string;
	logoUrl: string | null;
	logoAlt: string | null;
	footerMenu: MenuItem[];
	doulaMenu: MenuItem[];
	privacyMenu: MenuItem[];
};

const SETTINGS_PARAMS = qs.stringify(
	{
		fields: ["Title", "websiteDescription"],
		populate: {
			logo: { fields: ["url", "alternativeText", "width", "height"] },
			footerMenu: "*",
			doulaMenu: "*",
			privacyMenu: "*",
		},
	},
	{ encodeValuesOnly: true },
);

const SETTINGS_QUERY_OPTS = {
	staleTime: 1000 * 60 * 10,
	gcTime: 1000 * 60 * 60,
} as const;

function normalizeSettings(
	raw: RawSetting & { strapiId: number },
): SiteSettings {
	return {
		title: raw.Title,
		websiteDescription: raw.websiteDescription,
		logoUrl: raw.logo ? resolveMediaUrl(raw.logo.url) : null,
		logoAlt: raw.logo?.alternativeText ?? null,
		footerMenu: raw.footerMenu ?? [],
		doulaMenu: raw.doulaMenu ?? [],
		privacyMenu: raw.privacyMenu ?? [],
	};
}

export function useSettings() {
	return useQuery({
		queryKey: ["settings"],
		queryFn: async () => {
			const raw = await strapiGetSingle<RawSetting>(`/setting?${SETTINGS_PARAMS}`);
			return normalizeSettings(raw as RawSetting & { strapiId: number });
		},
		...SETTINGS_QUERY_OPTS,
	});
}
